import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AuthService from "../../../services/AuthService";
import { validatePassword } from "../../../services/passwordValidation";
import { usePagesTranslation } from "../../locales";

/**
 * Dialog to change the signed-in user's Cognito password (username/password accounts).
 */
const ChangePasswordDialog = ({ open, onClose }) => {
    const { t } = usePagesTranslation();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const baseKey = "profile.profileInfo.changePassword";

    useEffect(() => {
        if (!open) return;
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setSubmitting(false);
        setError(null);
        setSuccess(false);
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
    }, [open]);

    const handleSubmit = async () => {
        setError(null);
        const validation = validatePassword(newPassword);
        if (!validation.isValid) {
            setError(validation.errors.join(" "));
            return;
        }
        setSubmitting(true);
        try {
            const result = await AuthService.updateUserPassword(currentPassword, newPassword);
            if (result.success) {
                setSuccess(true);
            } else {
                setError(t(`${baseKey}.errors.${result.errorKey}`));
            }
        } catch (e) {
            setError(t(`${baseKey}.errors.generic`));
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            onClose();
        }
    };

    const passwordsMatch =
        newPassword.length > 0 &&
        confirmPassword.length > 0 &&
        newPassword === confirmPassword;
    const showMismatchAlert = confirmPassword.length > 0 && newPassword !== confirmPassword;
    const canUpdate = Boolean(currentPassword) && passwordsMatch && !submitting;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t(`${baseKey}.title`)}</DialogTitle>
            <DialogContent>
                {success ? (
                    <Typography variant="body1" color="success.main" sx={{ pt: 1 }}>
                        {t(`${baseKey}.success`)}
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t(`${baseKey}.requirementsHint`)}
                        </Typography>
                        <TextField
                            label={t(`${baseKey}.current`)}
                            type={showCurrentPassword ? "text" : "password"}
                            value={currentPassword}
                            onChange={(e) => {
                                setCurrentPassword(e.target.value);
                                setError(null);
                            }}
                            fullWidth
                            margin="dense"
                            autoComplete="current-password"
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={showCurrentPassword ? t(`${baseKey}.hide-password`) : t(`${baseKey}.show-password`)}
                                                onClick={() => setShowCurrentPassword((v) => !v)}
                                                edge="end"
                                                size="small"
                                            >
                                                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                        <TextField
                            label={t(`${baseKey}.new`)}
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                setError(null);
                            }}
                            fullWidth
                            margin="dense"
                            autoComplete="new-password"
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={showNewPassword ? t(`${baseKey}.hide-password`) : t(`${baseKey}.show-password`)}
                                                onClick={() => setShowNewPassword((v) => !v)}
                                                edge="end"
                                                size="small"
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                        <TextField
                            label={t(`${baseKey}.confirm`)}
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setError(null);
                            }}
                            fullWidth
                            margin="dense"
                            autoComplete="new-password"
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={showConfirmPassword ? t(`${baseKey}.hide-password`) : t(`${baseKey}.show-password`)}
                                                onClick={() => setShowConfirmPassword((v) => !v)}
                                                edge="end"
                                                size="small"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                        {showMismatchAlert && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {t(`${baseKey}.mismatch`)}
                            </Alert>
                        )}
                        {error && !showMismatchAlert && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {error}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {success ? (
                    <Button onClick={handleClose} variant="contained" sx={{ backgroundColor: "#3BA666", "&:hover": { backgroundColor: "#2e7d32" } }}>
                        {t(`${baseKey}.done`)}
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} disabled={submitting}>
                            {t(`${baseKey}.cancel`)}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={!canUpdate}
                            sx={{ backgroundColor: "#3BA666", "&:hover": { backgroundColor: "#2e7d32" } }}
                        >
                            {submitting ? t(`${baseKey}.saving`) : t(`${baseKey}.submit`)}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordDialog;
